<script>
  import { afterUpdate, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { format as formatDate } from 'date-fns';

  import { DatePicker } from '@client/components/datepicker';
  import { Icon } from '@client/components/icon';

  import { QueryParams } from '@client/utils/query-params';

  import { DATE_TODAY_TOKEN, resolveDateToken } from '@common/utils/dates';

  export let startDate = '';
  export let endDate = '';
  export let clearable = true;
  export let dateFormat = 'M/dd/yy';
  export let isRange = true;
  export let isOpen = false;
  export let showPresets = true;
  export let defaultYear = new Date().getFullYear();
  export let defaultMonth = new Date().getMonth();
  export let onDateChange = () => {};
  export let align = 'left';
  export let shareTodayToken = false;

  let initialized = false;
  let activeToken = null;
  let formattedStartDate = '';
  let formattedEndDate = '';

  const onClearDates = () => {
    startDate = '';
    endDate = '';
    activeToken = null;
    QueryParams.delete('startDate');
    QueryParams.delete('endDate');
    onDateChange({ startDate, endDate });
  };

  const initialize = () => {
    const startDateParam = QueryParams.get('startDate');
    const endDateParam = QueryParams.get('endDate');

    if (shareTodayToken && startDateParam === DATE_TODAY_TOKEN && endDateParam === DATE_TODAY_TOKEN) {
      activeToken = DATE_TODAY_TOKEN;
    }

    if (startDateParam) {
      startDate = resolveDateToken(startDateParam);
    } else {
      if (startDate) {
        QueryParams.set('startDate', startDate);
      } else {
        QueryParams.delete('startDate');
      }
    }

    if (endDateParam) {
      endDate = resolveDateToken(endDateParam);
    } else {
      if (endDate) {
        QueryParams.set('endDate', endDate);
      } else {
        QueryParams.delete('endDate');
      }
    }
  };

  let prevStartDate = '';
  let prevEndDate = '';

  const writeDateParam = (date) =>
    shareTodayToken && activeToken === DATE_TODAY_TOKEN ? DATE_TODAY_TOKEN : formatDateString(date);

  const updateQueryParams = () => {
    if (startDate) {
      QueryParams.set('startDate', writeDateParam(startDate));
    } else {
      QueryParams.delete('startDate');
    }

    if (endDate) {
      QueryParams.set('endDate', writeDateParam(endDate));
    } else {
      QueryParams.delete('endDate');
    }

    if ((isRange && startDate && endDate && (prevStartDate !== startDate || prevEndDate !== endDate)) || !isRange) {
      onDateChange({ startDate, endDate });
    }

    prevStartDate = startDate;
    prevEndDate = endDate;
  };

  // When the Today preset is clicked we write the keyword straight to the URL so a
  // shared link always resolves to the viewer's current day. writeDateParam re-encodes
  // the token in updateQueryParams so it survives later renders instead of being
  // overwritten with a literal date.
  const onPresetSelect = ({ dayOffset }) => {
    if (!shareTodayToken) {
      return;
    }

    if (dayOffset === 0) {
      activeToken = DATE_TODAY_TOKEN;
      QueryParams.set('startDate', DATE_TODAY_TOKEN);
      QueryParams.set('endDate', DATE_TODAY_TOKEN);
    } else {
      activeToken = null;
    }
  };

  const onDayClick = () => {
    activeToken = null;
  };

  const toggleDatePicker = () => {
    return startDate && !endDate ? (isOpen = true) : (isOpen = !isOpen);
  };

  const formatDateString = (dateString) => {
    if (!dateString) {
      return '';
    }

    return formatDate(new Date(dateString), dateFormat);
  };

  afterUpdate(() => {
    if (initialized) {
      updateQueryParams();
    }

    initialized = true;
  });

  $: formattedStartDate = formatDateString(startDate);
  $: formattedEndDate = formatDateString(endDate);

  onMount(initialize);
</script>

<div class="date-filter" class:active={startDate}>
  <DatePicker
    {isRange}
    isMultipane={false}
    bind:showPresets
    bind:startDate
    bind:isOpen
    bind:defaultMonth
    bind:defaultYear
    bind:endDate
    bind:align
    {onPresetSelect}
    {onDayClick}
    {...$$restProps}
  >
    <span on:click={toggleDatePicker} role="button" tabindex="0">
      <Icon name="calendar" style="margin-top: -1px" />
      <div class="dates">
        {#if startDate}
          {formattedStartDate} - {formattedEndDate}
        {:else}
          {$format('label.SELECT_DATES')}
        {/if}
      </div>
      {#if clearable && startDate}
        <span on:click|stopPropagation={onClearDates} role="button" tabindex="0">
          <Icon name="x" />
        </span>
      {/if}
    </span>
  </DatePicker>
</div>

<style lang="scss" src="./date.scss"></style>
